/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ToolSelect, ToolOption } from '../../../shared/components/ui/tool-select';
import { IntegrationClassification } from '../types/integration';
import { AgentIntegrationsService } from '../../Agents/services/agentIntegrationsService';
import { IntegrationBaseService } from '../services/integrationsBaseService';
import { getIntegrationDescription } from '@/shared/utils/i18nHelper';

// Tipo para o valor do IntegrationSelector
export type IntegrationSelectorValue = string | { name: string; agent_integration_id: string } | null;

interface IntegrationSelectorProps {
  /** ID do agente para buscar suas integrações */
  agentId: string;
  /** Filtro por classification (ex: "llm", "project_management") */
  filter?: IntegrationClassification;
  /** Valor atual (pode ser string ou objeto com name e agent_integration_id) */
  value?: IntegrationSelectorValue;
  /** Callback para mudança de valor (recebe string ou objeto com name e agent_integration_id) */
  onChange: (value: IntegrationSelectorValue) => void;
  /** Placeholder do campo */
  placeholder?: string;
  /** Se o campo está desabilitado */
  disabled?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

export const IntegrationSelector: React.FC<IntegrationSelectorProps> = ({
  agentId,
  filter,
  value,
  onChange,
  placeholder = "Selecione uma integração",
  disabled = false,
  className
}) => {
  const { i18n } = useTranslation();

  // Query para buscar integrações vinculadas ao agente
  const { data: availableIntegrations, isLoading } = useQuery({
    queryKey: ['integration-selector', agentId, filter],
    queryFn: async () => {
      try {
        if (!agentId) {
          return [];
        }

        // Buscar integrações vinculadas ao agente via agent_integrations
        const response = await AgentIntegrationsService.getByAgentId(agentId, true);

        // Como o backend não está retornando integration_base, buscaremos os dados separadamente
        if (!response.data || response.data.length === 0) {
          return [];
        }
        
        // Buscar todas as integrações base da organização
        const integrationBasesResponse = await IntegrationBaseService.getAll({
          status: 'active'
        });
        
        // Mapear as integrações com os dados completos da integration_base
        let integrations = response.data?.map(agentIntegration => {
          // Encontrar a integration_base correspondente
          const integrationBase = integrationBasesResponse.find(
            (base: any) => base.id === agentIntegration.integration_base_id
          );
          
          return {
            id: agentIntegration.integration_base_id,
            name: integrationBase?.name || agentIntegration.integration_name,
            label: integrationBase?.label || agentIntegration.integration_name,
            description: integrationBase ? getIntegrationDescription(integrationBase, i18n.language) || '' : '',
            logo_url: integrationBase?.logo_url || null,
            classification: integrationBase?.classification || '',
            status: integrationBase?.status || 'active',
            // Preservar o ID da relação agent_integration para uso em workflows
            agent_integration_id: agentIntegration.id
          };
        }) || [];

        // Aplicar filtro por classification se especificado
        if (filter) {
          integrations = integrations.filter(integration => 
            integration.classification === filter
          );
        }

        return integrations as any; // Retornar com agent_integration_id incluído
      } catch (error) {
        console.error('Erro ao buscar integrações para seletor:', error);
        return [];
      }
    },
    enabled: !!agentId
  });

  // Mapear integrações para formato ToolOption
  const options: ToolOption[] = availableIntegrations?.map((integration: any) => ({
    id: integration.name, // Nome técnico (ex: "openai", "azuredevops") 
    name: integration.label, // Nome amigável para exibição
    description: integration.description || '',
    logo_url: integration.logo_url,
    // Incluir agent_integration_id nos dados para uso posterior
    agent_integration_id: (integration as any).agent_integration_id
  })) || [];

  // Handler customizado para salvar objeto com name e agent_integration_id
  const handleValueChange = (selectedValue: string | null) => {
    if (!selectedValue || selectedValue === 'none') {
      onChange(null);
      return;
    }
    
    // Encontrar a opção selecionada para obter o agent_integration_id
    const selectedOption = options.find(option => option.id === selectedValue);
    if (selectedOption && selectedOption.agent_integration_id) {
      // Salvar objeto com name técnico e agent_integration_id
      onChange({
        name: selectedValue, // Nome técnico (ex: "openai")
        agent_integration_id: selectedOption.agent_integration_id
      });
    } else {
      // Fallback: salvar apenas o name se não tiver agent_integration_id
      onChange(selectedValue);
    }
  };
  
  // Determinar o valor atual para o ToolSelect (sempre string ou null)
  const selectValue: string | null = typeof value === 'object' && value !== null && 'name' in value ? value.name : (typeof value === 'string' ? value : null);

  return (
    <ToolSelect
      value={selectValue}
      onValueChange={handleValueChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      loading={isLoading}
      allowEmpty={true}
      emptyLabel="Nenhuma integração"
      className={className}
    />
  );
};

export default IntegrationSelector;
